/* candyearth: the Bundle Builder (operator 2026-10-07).
 *
 * One chain or bracelet + up to 4 charms, drawn as one picture from the makers'
 * own cut-outs at true size, priced by catalog/bundles.yaml. Three parts:
 *
 *   priceCart  -- the same reckoning as src/shop/bundles.py price_cart (the
 *                 reference, which the checkout service charges by); the
 *                 repo's tests hold both to the same cases.
 *   placements / draw -- where each charm hangs (on the clasp, or along the
 *                 chain) and the picture, as src/shop/bundles.py compose.
 *   Builder / Cart -- the page (sections/ce-bundle.liquid) and the cart page's
 *                 sets (sections/ce-cart-sets.liquid).
 *
 * The data (assets/ce-bundle.json, written by `shop bundle publish`): the
 * rules, and every piece: sku, kind, title, series, variant id, price in cents,
 * its picture (px_mm pixels to the millimetre), its hanging point (charms) or
 * hanging line and mode (chains, bracelets).
 */
(function (root) {
  'use strict';

  // --- the price ------------------------------------------------------------------

  function priceCart(lines, rules) {
    var bySet = {}, order = [], loose = [];
    lines.forEach(function (l) {
      if (l.set) {
        if (!bySet[l.set]) { bySet[l.set] = []; order.push(l.set); }
        bySet[l.set].push(l);
      } else loose.push(l);
    });
    var isBase = function (k) { return rules.base.indexOf(k) >= 0; };
    var sets = [], invalid = [], out = {};
    order.forEach(function (sid) {
      var ls = bySet[sid], bases = 0, charms = 0, others = 0, sub = 0;
      ls.forEach(function (l) {
        if (isBase(l.kind)) bases += l.qty;
        else if (l.kind === 'pendant') charms += l.qty;
        else others += l.qty;
        sub += l.price * l.qty;
      });
      if (bases === 1 && charms >= rules.min && charms <= rules.max && !others) {
        sets.push({ set: sid, lines: ls, charms: charms, subtotal: sub });
        return;
      }
      var reason = bases !== 1 ? 'needs exactly one chain or bracelet' :
        charms < rules.min ? 'needs at least ' + rules.min + ' charm' + (rules.min > 1 ? 's' : '') :
        charms > rules.max ? 'holds at most ' + rules.max + ' charms' : 'holds a piece that is not a charm';
      invalid.push({ set: sid, reason: reason });
      ls.forEach(function (l) { out[l.key] = { percent: 0 }; });
    });
    // two or more sets: every one of them gets the extra, flat
    var extra = sets.length >= 2 ? rules.extra_from_second_set : 0;
    sets.forEach(function (s) {
      s.percent = rules.percent_by_charms[String(s.charms)] + extra;
      s.lines.forEach(function (l) { out[l.key] = { percent: s.percent }; });
    });
    var pieces = loose.reduce(function (n, l) { return n + l.qty; }, 0);
    var lpct = pieces > rules.loose_more_than ? rules.loose_percent : 0;
    loose.forEach(function (l) { out[l.key] = { percent: lpct }; });
    var disc = 0, subtotal = 0;
    lines.forEach(function (l) {
      var bp = Math.round(out[l.key].percent * 100);
      var unit = Math.floor((l.price * bp + 5000) / 10000);    // per unit, whole cents, half up
      out[l.key].unit_discount = unit;
      out[l.key].discount = unit * l.qty;
      disc += unit * l.qty;
      subtotal += l.price * l.qty;
    });
    sets.forEach(function (s) {
      s.discount = s.lines.reduce(function (n, l) { return n + out[l.key].discount; }, 0);
      delete s.lines;
    });
    return { lines: out, sets: sets, invalid: invalid, loose: { pieces: pieces, percent: lpct },
             subtotal: subtotal, discount: disc, total: subtotal - disc, blocked: invalid.length > 0 };
  }

  // --- where charms hang ----------------------------------------------------------

  var GAP_MM = 3.0, FAN_OVERLAP = 0.75, FAN_MAX_DEG = 50.0, OVAL_SIDE_DEG = 30.0, LIFT_MM = 0.4;

  /* The point `dist` pixels along the hanging line (negative = left), from its
     lowest point ('low': a drape) or its middle across ('mid': a clasp, whose
     gate or knob can make one side the lowest). */
  function lineAt(line, dist, origin) {
    var s = [0], low = 0, lowY = -Infinity, i;
    for (i = 1; i < line.length; i++) {
      s.push(s[i - 1] + Math.hypot(line[i][0] - line[i - 1][0], line[i][1] - line[i - 1][1]));
    }
    if (origin === 'mid') {
      var xmin = Infinity, xmax = -Infinity, best = Infinity;
      line.forEach(function (p) { xmin = Math.min(xmin, p[0]); xmax = Math.max(xmax, p[0]); });
      for (i = 0; i < line.length; i++) {
        var dx = Math.abs(line[i][0] - (xmin + xmax) / 2);
        if (dx < best) { best = dx; low = s[i]; }
      }
    } else {
      for (i = 0; i < line.length; i++) if (line[i][1] > lowY) { lowY = line[i][1]; low = s[i]; }
    }
    var t = Math.min(Math.max(low + dist, 0), s[s.length - 1]);
    for (i = 1; i < line.length; i++) {
      if (s[i] >= t) {
        var f = s[i] === s[i - 1] ? 0 : (t - s[i - 1]) / (s[i] - s[i - 1]);
        return [line[i - 1][0] + f * (line[i][0] - line[i - 1][0]),
                line[i - 1][1] + f * (line[i][1] - line[i - 1][1])];
      }
    }
    return line[line.length - 1].slice();
  }

  /* The operator's arrangements (2026-10-08), as src/shop/bundles.py placements:
     a ROUND clasp fans the charms out from the ring's middle, a little over each
     other (the outermost at most FAN_MAX_DEG from straight down); an OVAL clasp
     hangs the middle ones straight and leans the outer two OVAL_SIDE_DEG out; a
     plain chain spaces them out, each square to the chain. rot is in degrees
     counter-clockwise: positive swings a charm's bottom to the right. */
  function placements(chain, charms, pxmm) {
    var k = charms.length, out = [], i;
    var steps = [];
    for (i = 1; i < k; i++) steps.push(FAN_OVERLAP * (charms[i - 1].w + charms[i].w) / 2);
    var spread = function (list) {
      var at = [0];
      list.forEach(function (d, n) { at.push(at[n] + d); });
      var mid = k ? at[k - 1] / 2 : 0;
      return at.slice(0, k).map(function (v) { return v - mid; });
    };
    if (chain.mode === 'along') {
      var gaps = [];
      for (i = 1; i < k; i++) gaps.push((charms[i - 1].w + charms[i].w) / 2 + GAP_MM * pxmm);
      spread(gaps).forEach(function (d, n) {
        var p = lineAt(chain.line, d), a = lineAt(chain.line, d - 3), b = lineAt(chain.line, d + 3);
        var tilt = (a[0] === b[0] && a[1] === b[1]) ? 0 : -Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI;
        out.push({ x: p[0], y: p[1], rot: tilt, z: n });
      });
    } else if (chain.shape === 'round' && chain.center) {
      var cx = chain.center[0], cy = chain.center[1], m = lineAt(chain.line, 0, 'mid');
      var r = Math.max(1, Math.hypot(m[0] - cx, m[1] - cy));
      var ang = spread(steps).map(function (v) { return v / r; });
      var topA = FAN_MAX_DEG * Math.PI / 180;
      if (k > 1 && ang[k - 1] > topA) {
        var f = topA / ang[k - 1];
        ang = ang.map(function (v) { return v * f; });
      }
      ang.forEach(function (a, n) {
        var off = n - (k - 1) / 2;
        out.push({ x: cx + r * Math.sin(a), y: cy + r * Math.cos(a), rot: a * 180 / Math.PI, z: -Math.abs(off) });
      });
    } else {
      var at = spread(steps), len = 0;
      for (i = 1; i < chain.line.length; i++) {
        len += Math.hypot(chain.line[i][0] - chain.line[i - 1][0], chain.line[i][1] - chain.line[i - 1][1]);
      }
      var room = 0.9 * len / 2;
      if (k > 1 && at[k - 1] > room) {
        var g = room / at[k - 1];
        at = at.map(function (v) { return v * g; });
      }
      at.forEach(function (d, n) {
        var off = n - (k - 1) / 2, q = lineAt(chain.line, d, 'mid');
        var lean = k >= 3 && (n === 0 || n === k - 1) ? OVAL_SIDE_DEG * Math.sign(off) : 0;
        out.push({ x: q[0], y: q[1], rot: lean, z: -Math.abs(off) });
      });
    }
    return out;
  }

  // --- the picture ----------------------------------------------------------------

  var cache = {};
  function image(url) {
    if (!cache[url]) {
      cache[url] = new Promise(function (ok, bad) {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = function () { ok(im); };
        im.onerror = function () { bad(new Error('picture did not load: ' + url)); };
        im.src = url;
      });
    }
    return cache[url];
  }

  /* The set on `canvas`, framed on the charms: the bottom of the chain and the
     clasp, not the whole chain the maker photographed. */
  function draw(canvas, chain, charms, pxmm, bg) {
    var urls = [chain.file].concat(charms.map(function (c) { return c.file; }));
    return Promise.all(urls.map(image)).then(function (ims) {
      // a charm's top sits half a tube and a little more above the line, so its
      // loop -- drawn over the chain below -- wraps the tube (operator 2026-10-08)
      var spots = placements(chain, charms, pxmm);
      var sink = Math.min(chain.tube || 1.5 * pxmm, 4 * pxmm) / 2 + LIFT_MM * pxmm;
      // the frame, in the chain picture's pixels: as wide as the charms need
      // (30 to 46 mm each side of the middle), some chain above, the charms below
      var low = lineAt(chain.line, 0, chain.mode === 'clasp' ? 'mid' : 'low');
      var bottom = low[1] + 12 * pxmm, left = low[0], right = low[0];
      charms.forEach(function (c, i) {
        bottom = Math.max(bottom, spots[i].y - sink - c.hang[1] + c.h + 4 * pxmm);
        left = Math.min(left, spots[i].x - c.hang[0]);
        right = Math.max(right, spots[i].x - c.hang[0] + c.w);
      });
      var mid = (left + right) / 2;
      var halfW = Math.min(46 * pxmm, Math.max(30 * pxmm, (right - left) / 2 + 14 * pxmm));
      var top = Math.max(-10 * pxmm, low[1] - 1.2 * halfW);
      var x0 = Math.max(-10 * pxmm, mid - halfW), x1 = Math.min(chain.w + 10 * pxmm, mid + halfW);
      var fw = x1 - x0, fh = bottom - top;
      var cssW = canvas.clientWidth || 600, ratio = window.devicePixelRatio || 1;
      var scale = cssW * ratio / fw;
      canvas.width = Math.round(fw * scale);
      canvas.height = Math.round(fh * scale);
      canvas.style.aspectRatio = fw + ' / ' + fh;
      var ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      // an opaque chain (its photograph, marks erased -- operator 2026-10-18: chains
      // need no cut-out, "we only overlap charms on top") brings its paper's colour
      // as the ground and throws no shadow
      ctx.fillStyle = (chain.opaque && chain.bg) || bg || '#f6f1ea';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(scale, 0, 0, scale, -x0 * scale, -top * scale);
      ctx.shadowColor = 'rgba(60, 40, 20, 0.26)';
      ctx.shadowBlur = 12 * scale;
      ctx.shadowOffsetX = 0.25 * pxmm * scale;
      ctx.shadowOffsetY = 0.5 * pxmm * scale;
      var idx = charms.map(function (_, i) { return i; });
      idx.sort(function (a, b) { return spots[a].z - spots[b].z; });
      // the chain, then the charms whole on top of it and its clasp -- loops
      // included (operator 2026-10-08: "laid on top of the chain and loop, not
      // under"); a loop's top sits just above the tube, so it reads as hooked on
      if (chain.opaque) { ctx.save(); ctx.shadowColor = 'transparent'; }
      ctx.drawImage(ims[0], 0, 0, chain.w, chain.h);
      if (chain.opaque) ctx.restore();
      idx.forEach(function (i) {
        var c = charms[i], s = spots[i];
        ctx.save();
        ctx.translate(s.x, s.y - sink);
        ctx.rotate(-s.rot * Math.PI / 180);        // the canvas turns clockwise
        ctx.drawImage(ims[i + 1], -c.hang[0], -c.hang[1], c.w, c.h);
        ctx.restore();
      });
    });
  }

  // --- small helpers --------------------------------------------------------------

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function getJSON(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) return r.json().catch(function () { return {}; }).then(function (b) {
        throw new Error(b.description || b.message || b.error || ('HTTP ' + r.status));
      });
      return r.json();
    });
  }
  function postJSON(url, body) {
    return getJSON(url, { method: 'POST', headers: { 'Content-Type': 'application/json',
                                                      Accept: 'application/json' },
                          body: JSON.stringify(body) });
  }
  function indexData(data) {
    data.bySku = {};
    data.byVariant = {};
    data.pieces.forEach(function (p) {
      data.bySku[p.sku] = p;
      data.byVariant[String(p.variant)] = p;
    });
    return data;
  }
  /* The cart's lines in priceCart's terms. */
  function cartLines(cart, data) {
    return cart.items.map(function (it) {
      var p = data.byVariant[String(it.variant_id)];
      return { key: it.key, kind: p ? p.kind : 'other', price: it.price, qty: it.quantity,
               set: (it.properties && it.properties._set) || '' };
    });
  }
  /* A set's lines, in the order the set was built. */
  function setLines(cart, sid) {
    return cart.items.filter(function (it) { return it.properties && it.properties._set === sid; })
      .sort(function (a, b) { return Number(a.properties._pos || 0) - Number(b.properties._pos || 0); });
  }
  /* The number in a set's name ("Jewelry set 3" -> 3); unnamed sets go last. */
  function setNumber(cart, sid) {
    var l = setLines(cart, sid)[0];
    var m = l && /(\d+)\s*$/.exec((l.properties && l.properties.Set) || '');
    return m ? Number(m[1]) : 1e9;
  }
  function setIds(cart) {
    var seen = [];
    cart.items.forEach(function (it) {
      var s = it.properties && it.properties._set;
      if (s && seen.indexOf(s) < 0) seen.push(s);
    });
    return seen;
  }

  // --- the builder ----------------------------------------------------------------

  function Builder(root, data) {
    this.root = root;
    this.data = data;
    this.rules = data.rules;
    this.base = null;
    this.charms = [];
    this.cart = { items: [] };
    this.edit = new URLSearchParams(location.search).get('edit') || '';
    this.perPage = parseInt(root.dataset.perPage, 10) || 24;
    this.find();
    this.bind();
    var self = this;
    getJSON('/cart.js').then(function (c) { self.cart = c; self.restore(); })
      .catch(function () { self.restore(); });
  }

  Builder.prototype.find = function () {
    var r = this.root;
    this.$base = r.querySelector('[data-slot-base]');
    this.$charms = Array.prototype.slice.call(r.querySelectorAll('[data-slot-charm]'));
    this.$canvas = r.querySelector('[data-picture]');
    this.$empty = r.querySelector('[data-picture-empty]');
    this.$price = r.querySelector('[data-price]');
    this.$add = r.querySelector('[data-add]');
    this.$msg = r.querySelector('[data-message]');
    this.$dialog = r.querySelector('[data-picker]');
  };

  Builder.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-remove]');
      if (rm) {
        self.charms.splice(Number(rm.dataset.remove), 1);
        self.changed();
        return;
      }
      var pick = e.target.closest('[data-pick]');
      if (pick) self.pick(pick.dataset.pick);
    });
    this.$add.addEventListener('click', function () { self.addToCart(); });
    var redraw;
    window.addEventListener('resize', function () {
      clearTimeout(redraw);
      redraw = setTimeout(function () { self.drawPicture(); }, 150);
    });
  };

  /* The set to start from: the one being edited (from the cart), else the
     link's (#b=SKU&c=SKU,SKU), else the last draft. */
  Builder.prototype.restore = function () {
    var self = this, want = null;
    if (this.edit) {
      var lines = setLines(this.cart, this.edit);
      if (lines.length) {
        want = { b: null, c: [] };
        lines.forEach(function (it) {
          var p = self.data.byVariant[String(it.variant_id)];
          if (!p) return;
          for (var n = 0; n < it.quantity; n++) {
            if (p.kind === 'pendant') want.c.push(p.sku); else want.b = p.sku;
          }
        });
      } else this.edit = '';
    }
    if (!want) {
      var h = new URLSearchParams(location.hash.slice(1));
      if (h.get('b') || h.get('c')) want = { b: h.get('b'), c: (h.get('c') || '').split(',') };
    }
    if (!want) {
      try { want = JSON.parse(localStorage.getItem('ce-bundle-draft') || 'null'); } catch (e) { want = null; }
    }
    if (want) {
      var b = this.data.bySku[want.b];
      this.base = b && b.kind !== 'pendant' ? b : null;
      this.charms = (want.c || []).map(function (s) { return self.data.bySku[s]; })
        .filter(function (p) { return p && p.kind === 'pendant'; }).slice(0, this.rules.max);
    }
    if (this.edit) this.$add.textContent = this.$add.dataset.editLabel || 'Update the set';
    this.changed(true);
  };

  Builder.prototype.changed = function (quiet) {
    var draft = { b: this.base && this.base.sku, c: this.charms.map(function (c) { return c.sku; }) };
    try { localStorage.setItem('ce-bundle-draft', JSON.stringify(draft)); } catch (e) { /* private window */ }
    var hash = (draft.b ? 'b=' + draft.b : '') + (draft.c.length ? '&c=' + draft.c.join(',') : '');
    history.replaceState(null, '', location.pathname + location.search + (hash ? '#' + hash.replace(/^&/, '') : ''));
    this.renderSlots();
    this.renderPrice();
    this.drawPicture();
    if (!quiet) this.say('');
  };

  /* A slot: a button that opens the picture sheet (showing the piece, or
     "+ Choose ..."), and beside it, for a charm, a button that takes it off. */
  Builder.prototype.renderSlots = function () {
    var self = this;
    fill(this.$base, this.base, 'base', -1);
    this.$charms.forEach(function (s, i) { fill(s, self.charms[i], 'charms', i); });
    function fill(slot, piece, what, i) {
      slot.innerHTML = '';
      slot.classList.toggle('is-filled', !!piece);
      var b = el('button', 'ce-bundle__slot-pick');
      b.type = 'button';
      b.setAttribute('data-pick', what);
      if (!piece) {
        b.appendChild(el('span', 'ce-bundle__plus', '+'));
        b.appendChild(el('span', 'ce-bundle__slot-label', slot.dataset.empty));
        slot.appendChild(b);
        return;
      }
      b.setAttribute('aria-label', 'Change ' + piece.title);
      var im = el('img', 'ce-bundle__slot-img');
      im.src = piece.thumb || piece.file; im.alt = ''; im.loading = 'lazy';
      b.appendChild(im);
      var tx = el('span', 'ce-bundle__slot-text');
      tx.appendChild(el('span', 'ce-bundle__slot-title', piece.title));
      tx.appendChild(el('span', 'ce-bundle__slot-price', money(piece.price)));
      b.appendChild(tx);
      slot.appendChild(b);
      if (i >= 0) {
        var x = el('button', 'ce-bundle__remove', '\u00d7');
        x.type = 'button';
        x.setAttribute('data-remove', String(i));
        x.setAttribute('aria-label', 'Remove ' + piece.title);
        slot.appendChild(x);
      }
    }
  };

  /* This set priced inside the whole cart, so the extra for a second set shows
     here as it will at checkout (a set being edited is left out of the cart). */
  Builder.prototype.quote = function () {
    var self = this, sid = '__draft__';
    var lines = cartLines(this.cart, this.data).filter(function (l) {
      return !self.edit || l.set !== self.edit;
    });
    var mine = [];
    if (this.base) mine.push({ key: 'draft-base', kind: this.base.kind, price: this.base.price, qty: 1, set: sid });
    this.charms.forEach(function (c, i) {
      mine.push({ key: 'draft-' + i, kind: 'pendant', price: c.price, qty: 1, set: sid });
    });
    var before = priceCart(lines, this.rules);
    var all = priceCart(lines.concat(mine), this.rules);
    var sub = 0, disc = 0, pct = 0;
    mine.forEach(function (l) { sub += l.price; disc += all.lines[l.key].discount; pct = all.lines[l.key].percent; });
    var others = all.sets.filter(function (s) { return s.set !== sid; }).length;
    // what adding this set changes on the rest of the cart (the extra moving
    // onto another set, single pieces no longer counted as loose...)
    var elsewhere = all.discount - disc - before.discount;
    return { subtotal: sub, discount: disc, percent: pct, total: sub - disc, otherSets: others,
             elsewhere: elsewhere };
  };

  Builder.prototype.renderPrice = function () {
    var q = this.quote(), box = this.$price, r = this.rules;
    box.innerHTML = '';
    var row = function (label, value, cls) {
      var d = el('div', 'ce-bundle__row' + (cls ? ' ' + cls : ''));
      d.appendChild(el('span', '', label));
      d.appendChild(el('span', '', value));
      box.appendChild(d);
    };
    row('Pieces', money(q.subtotal));
    if (!this.base || !this.charms.length) {
      box.appendChild(el('p', 'ce-bundle__hint',
        'Choose a chain or bracelet and at least one charm: ' + r.percent_by_charms['1'] + '% off with one charm, up to ' +
        r.percent_by_charms[String(r.max)] + '% with ' + r.max + '.'));
    } else {
      var label = 'Set discount, ' + this.charms.length + (this.charms.length === 1 ? ' charm' : ' charms') +
        ' (' + fmtPct(q.percent) + ')';
      row(label, '−' + money(q.discount), 'is-saving');
      row('Your set', money(q.total), 'is-total');
      if (q.otherSets) {
        box.appendChild(el('p', 'ce-bundle__hint',
          'With two or more sets in your cart, every set gets an extra ' +
          fmtPct(r.extra_from_second_set) + ' off.'));
      }
      if (q.elsewhere > 0 && !this.edit) {
        box.appendChild(el('p', 'ce-bundle__hint is-saving',
          'Adding it also takes ' + money(q.elsewhere) + ' off the rest of your cart.'));
      }
      if (this.charms.length < r.max) {
        // one more charm moves the set up a level; any extra it gets stays with it
        var extra = q.percent - r.percent_by_charms[String(this.charms.length)];
        box.appendChild(el('p', 'ce-bundle__hint', 'Add another charm for ' +
          fmtPct(r.percent_by_charms[String(this.charms.length + 1)] + extra) + ' off the set.'));
      }
    }
    this.$add.disabled = !(this.base && this.charms.length >= r.min);
  };

  function fmtPct(p) { return (Math.round(p * 10) / 10) + '%'; }

  Builder.prototype.drawPicture = function () {
    var ready = this.base && this.base.mode;
    this.$empty.hidden = !!ready;
    this.$canvas.hidden = !ready;
    if (!ready) return;
    var self = this, token = (this._token = (this._token || 0) + 1);
    draw(this.$canvas, this.base, this.charms, this.data.px_mm).catch(function (e) {
      if (token === self._token) self.say(e.message);
    });
  };

  Builder.prototype.say = function (text, good) {
    this.$msg.textContent = text;
    this.$msg.classList.toggle('is-good', !!good);
  };

  // --- the picture sheets (one chain, or up to 4 charms) ---------------------------

  Builder.prototype.pick = function (what) {
    var self = this, d = this.$dialog, r = this.rules;
    var multi = what === 'charms';
    var pool = this.data.pieces.filter(function (p) {
      return multi ? p.kind === 'pendant' : r.base.indexOf(p.kind) >= 0;
    });
    var chosen = multi ? this.charms.map(function (c) { return c.sku; }) :
      (this.base ? [this.base.sku] : []);
    var series = [];
    pool.forEach(function (p) { if (series.indexOf(p.series) < 0) series.push(p.series); });
    var tab = multi ? series[0] : 'All', page = 0;
    var $title = d.querySelector('[data-picker-title]');
    var $tabs = d.querySelector('[data-picker-tabs]');
    var $grid = d.querySelector('[data-picker-grid]');
    var $pager = d.querySelector('[data-picker-pager]');
    var $count = d.querySelector('[data-picker-count]');
    var $done = d.querySelector('[data-picker-done]');
    $title.textContent = multi ? 'Choose up to ' + r.max + ' charms' : 'Choose a chain or bracelet';

    // the tabs are one line that scrolls sideways (more than seven collections, 2026-10-18):
    // the chosen one is brought into view, and an edge fades where more lie beyond it
    function fade() {
      var left = $tabs.scrollLeft, more = $tabs.scrollWidth - $tabs.clientWidth - left;
      $tabs.style.setProperty('--fade-l', left > 4 ? '4rem' : '0rem');
      $tabs.style.setProperty('--fade-r', more > 4 ? '4rem' : '0rem');
    }
    if (!$tabs.dataset.fades) {
      $tabs.dataset.fades = '1';
      $tabs.addEventListener('scroll', fade);
      window.addEventListener('resize', fade);
    }
    function tabs() {
      $tabs.innerHTML = '';
      var on = null;
      (multi ? series : ['All'].concat(series)).forEach(function (s) {
        var b = el('button', 'ce-bundle__tab' + (s === tab ? ' is-on' : ''), s);
        b.type = 'button';
        b.setAttribute('aria-pressed', s === tab ? 'true' : 'false');
        b.addEventListener('click', function () { tab = s; page = 0; tabs(); grid(); });
        $tabs.appendChild(b);
        if (s === tab) on = b;
      });
      if (on) {                                   // only when it is out of view: no other scrolling
        var cr = $tabs.getBoundingClientRect(), br = on.getBoundingClientRect();
        if (br.right > cr.right - 8) $tabs.scrollLeft += br.right - cr.right + 24;
        else if (br.left < cr.left + 8) $tabs.scrollLeft -= cr.left - br.left + 24;
      }
      fade();
      if (window.requestAnimationFrame) requestAnimationFrame(fade);   // once the sheet is laid out
    }
    function shown() {
      return pool.filter(function (p) { return tab === 'All' || p.series === tab; });
    }
    function grid() {
      var list = shown(), pages = Math.max(1, Math.ceil(list.length / self.perPage));
      page = Math.min(page, pages - 1);
      $grid.innerHTML = '';
      list.slice(page * self.perPage, (page + 1) * self.perPage).forEach(function (p) {
        var on = chosen.indexOf(p.sku) >= 0;
        var full = multi && !on && chosen.length >= r.max;
        var b = el('button', 'ce-bundle__tile' + (on ? ' is-on' : '') + (full ? ' is-full' : ''));
        b.type = 'button';
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (full) b.title = r.max + ' charms chosen: take one off to add another';
        var im = el('img');
        im.src = p.thumb || p.file; im.alt = ''; im.loading = 'lazy';
        b.appendChild(im);
        b.appendChild(el('span', 'ce-bundle__tile-title', p.title));
        b.appendChild(el('span', 'ce-bundle__tile-price', money(p.price)));
        b.addEventListener('click', function () {
          var at = chosen.indexOf(p.sku);
          if (multi) {
            if (at >= 0) chosen.splice(at, 1);
            else if (chosen.length < r.max) chosen.push(p.sku);
          } else chosen = at >= 0 ? [] : [p.sku];
          grid();
        });
        $grid.appendChild(b);
      });
      $pager.innerHTML = '';
      if (pages > 1) {
        for (var i = 0; i < pages; i++) {
          (function (i) {
            var b = el('button', 'ce-bundle__page' + (i === page ? ' is-on' : ''), String(i + 1));
            b.type = 'button';
            b.setAttribute('aria-label', 'Page ' + (i + 1));
            b.addEventListener('click', function () { page = i; grid(); $grid.scrollTop = 0; });
            $pager.appendChild(b);
          })(i);
        }
      }
      $count.textContent = multi ? chosen.length + ' of ' + r.max + ' chosen' :
        (chosen.length ? '1 chosen' : 'None chosen');
    }
    $done.onclick = function () {
      if (multi) {
        self.charms = chosen.map(function (s) { return self.data.bySku[s]; });
      } else {
        self.base = chosen.length ? self.data.bySku[chosen[0]] : null;
      }
      d.close();
      self.changed();
    };
    d.querySelector('[data-picker-cancel]').onclick = function () { d.close(); };
    tabs();
    grid();
    d.showModal();
  };

  // --- into the cart --------------------------------------------------------------

  Builder.prototype.addToCart = function () {
    var self = this;
    if (!this.base || this.charms.length < this.rules.min) return;
    this.$add.disabled = true;
    var sid = this.edit || ('S' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)).toUpperCase();
    // an edited set keeps its name; a new one takes the next number not in the cart
    var label = (this.edit && (setLines(this.cart, this.edit)[0] || {}).properties || {}).Set;
    if (!label) {
      var top = setIds(this.cart).reduce(function (n, s) {
        var k = setNumber(self.cart, s);
        return k < 1e9 ? Math.max(n, k) : n;
      }, 0);
      label = 'Jewelry set ' + (top + 1);
    }
    // one line per piece, each with its slot (_pos): the set comes back from
    // the cart in the order it was built, the same charm twice included
    var items = [this.base].concat(this.charms).map(function (p, i) {
      return { id: p.variant, quantity: 1, properties: { _set: sid, _pos: String(i), Set: label } };
    });
    var clear = Promise.resolve();
    if (this.edit) {
      var updates = {};
      this.cart.items.forEach(function (it) {
        if (it.properties && it.properties._set === self.edit) updates[it.key] = 0;
      });
      clear = postJSON('/cart/update.js', { updates: updates });
    }
    clear.then(function () { return postJSON('/cart/add.js', { items: items }); })
      .then(function () {
        try { localStorage.removeItem('ce-bundle-draft'); } catch (e) { /* private window */ }
        location.href = '/cart';
      })
      .catch(function (e) {
        self.say('Could not add the set: ' + e.message);
        self.$add.disabled = false;
      });
  };

  // --- the cart page: each set as one item ------------------------------------------

  var HANDOFF = 'ce-cart-handoff', HANDOFF_HOURS = 12;
  // Pieces no longer for sale (operator 2026-10-17: "If shopping cart contains retired
  // items, we have to flag it"): a wave's collections come down, and their pieces with
  // them. Each line's product is asked for (/products/<handle>.js answers only while
  // it is on the store); what was in the cart last time (SEEN) tells a piece Shopify
  // took out by itself from one the shopper removed.
  var SEEN = 'ce-cart-seen', SEEN_DAYS = 30;

  function Cart(root, data) {
    this.root = root;
    this.data = data;
    this.builder = root.dataset.builder || '/pages/create-your-jewelry';
    this.service = root.dataset.checkout || '';
    var self = this;
    this.refresh();
    document.addEventListener('ce-cart-changed', function () { self.refresh(); });
    var items = document.getElementById('main-cart-items');
    if (items && 'MutationObserver' in window) {
      var t;
      this.observer = new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(function () { self.refresh(); }, 120);
      });
      this.observer.observe(items, { childList: true, subtree: true });
    }
  }

  Cart.prototype.refresh = function () {
    var self = this;
    if (this._busy) return;
    this._busy = true;
    getJSON('/cart.js').then(function (cart) {
      self.cart = cart;
      self.render();
    }).catch(function () { /* the theme's own cart still stands */ })
      .then(function () { self._busy = false; });
  };

  /* A single piece's row with its discount: the line total and the unit price as the
     theme shows a sale (struck through, then the new price), plus "10% off". The
     theme's own markup is kept aside and put back when the discount goes. */
  var kept = typeof WeakMap === 'function' ? new WeakMap() : null;
  function markLoose(row, it, l) {
    var key = l.discount ? l.discount + '/' + it.final_line_price : '';
    var spots = [];
    Array.prototype.forEach.call(row.querySelectorAll('.cart-item__price-wrapper'), function (w) {
      var cur = w.querySelector(':scope > [data-ce-price]') ||
        w.querySelector(':scope > .price, :scope > .cart-item__discounted-prices');
      if (cur) spots.push({ node: cur, line: true });
    });
    var det = row.querySelector('.cart-item__details');
    if (det) {
      var unit = det.querySelector(':scope > [data-ce-price]') ||
        det.querySelector(':scope > .product-option, :scope > .cart-item__discounted-prices');
      if (unit) spots.push({ node: unit, line: false });
    }
    spots.forEach(function (s) {
      var node = s.node;
      if ((node.getAttribute('data-ce-price') || '') === key) return;          // as it should be
      var orig = node.hasAttribute('data-ce-price') && kept ? kept.get(node) : node;
      if (!key) {                                                            // no discount: theirs back
        if (orig && orig !== node) node.replaceWith(orig);
        return;
      }
      var mine;
      if (s.line) {
        mine = el('dl', 'cart-item__discounted-prices');
        mine.innerHTML = '<dt class="visually-hidden">Regular price</dt><dd><s class="cart-item__old-price price price--end"></s></dd>' +
          '<dt class="visually-hidden">Sale price</dt><dd class="price price--end"></dd>';
        mine.querySelector('s').textContent = money(it.original_line_price != null ? it.original_line_price
                                                                                   : it.price * it.quantity);
        mine.querySelector('dd.price').textContent = money(it.final_line_price - l.discount);
      } else {
        mine = el('div', 'cart-item__discounted-prices');
        var was = el('s', 'cart-item__old-price product-option', money(it.price));
        var now = el('strong', 'cart-item__final-price product-option', money(it.price - l.unit_discount));
        mine.appendChild(was);
        mine.appendChild(document.createTextNode(' '));
        mine.appendChild(now);
        mine.appendChild(el('span', 'ce-loose-off', fmtPct(l.percent) + ' off'));
      }
      mine.setAttribute('data-ce-price', key);
      if (kept) kept.set(mine, orig);
      node.replaceWith(mine);
    });
  }

  /* An empty cart right after a checkout through the service: offer the copy back
     (for a customer who left the checkout without paying), for HANDOFF_HOURS. */
  Cart.prototype.offerBack = function () {
    var self = this, msg = this.root.querySelector('[data-message]'), saved = null;
    try { saved = JSON.parse(localStorage.getItem(HANDOFF) || 'null'); } catch (e) { saved = null; }
    if (!saved || !msg) return;
    if (this.cart.item_count || Date.now() - saved.at > HANDOFF_HOURS * 3600 * 1000) {
      try { localStorage.removeItem(HANDOFF); } catch (e) { /* nothing kept */ }
      return;
    }
    msg.textContent = 'Left the checkout without paying? ';
    var back = el('button', 'link', 'Bring back your cart');
    back.type = 'button';
    back.addEventListener('click', function () {
      back.disabled = true;
      postJSON('/cart/add.js', { items: saved.items }).then(function () {
        try { localStorage.removeItem(HANDOFF); } catch (e) { /* nothing kept */ }
        location.reload();
      }).catch(function (e) { msg.textContent = 'Could not bring the cart back: ' + e.message; });
    });
    msg.appendChild(back);
  };

  Cart.prototype.checkGone = function () {
    var self = this, cart = this.cart, prev = null, now = {};
    try { prev = JSON.parse(localStorage.getItem(SEEN) || 'null'); } catch (e) { prev = null; }
    cart.items.forEach(function (it) {
      now[it.variant_id] = { title: it.product_title, handle: it.handle, set: (it.properties && it.properties.Set) || '' };
    });
    var dropped = prev && prev.lines && Date.now() - prev.at < SEEN_DAYS * 86400000
      ? Object.keys(prev.lines).filter(function (v) { return !now[v]; }).map(function (v) { return prev.lines[v]; }) : [];
    try { localStorage.setItem(SEEN, JSON.stringify({ at: Date.now(), lines: now })); } catch (e) { /* not kept */ }
    var ask = this.onStore || (this.onStore = {});
    var handles = cart.items.map(function (it) { return it.handle; }).concat(dropped.map(function (d) { return d.handle; }))
      .filter(function (h, i, all) { return h && all.indexOf(h) === i && !(h in ask); });
    Promise.all(handles.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { ask[h] = r.status !== 404; })
        .catch(function () { ask[h] = true; });            // not known: not flagged
    })).then(function () {
      self.gone = cart.items.filter(function (it) { return ask[it.handle] === false; });
      self.dropped = (self.dropped || []).concat(dropped.filter(function (d) { return ask[d.handle] === false; }));
      self.showGone();
    });
  };

  Cart.prototype.showGone = function () {
    var self = this, box = this.root.querySelector('[data-ce-gone]');
    if (!box) {
      box = el('div', 'ce-gone');
      box.setAttribute('data-ce-gone', '');
      box.setAttribute('role', 'alert');
      this.root.insertBefore(box, this.root.firstChild);
    }
    box.innerHTML = '';
    var name = function (x) { return x.title + (x.set ? ' (from ' + x.set + ')' : ''); };
    // what the sweep took out is told there, with what to put in its place (2026-10-17)
    var swept = null;
    try { swept = JSON.parse(sessionStorage.getItem(SWEPT) || 'null'); } catch (e) { swept = null; }
    var told = ((swept && swept.notice && swept.notice.removed) || []).map(function (r) { return r.title; });
    this.dropped = (this.dropped || []).filter(function (d) { return told.indexOf(d.title) < 0; });
    if ((this.dropped || []).length) {
      box.appendChild(el('p', '', 'Taken out of your cart, no longer available: ' +
        this.dropped.map(name).join(', ') + '. A set it was part of may now save less, or need another charm.'));
    }
    var gone = this.gone || [];
    if (gone.length) {
      box.appendChild(el('p', '', 'No longer available: ' + gone.map(function (it) {
        return name({ title: it.product_title, set: (it.properties && it.properties.Set) || '' });
      }).join(', ') + '. Remove ' + (gone.length > 1 ? 'them' : 'it') + ' to check out.'));
      var rm = el('button', 'button button--secondary', gone.length > 1 ? 'Remove them' : 'Remove it');
      rm.type = 'button';
      rm.addEventListener('click', function () {
        var updates = {};
        gone.forEach(function (it) { updates[it.key] = 0; });
        rm.disabled = true;
        postJSON('/cart/update.js', { updates: updates }).then(function () { location.reload(); });
      });
      box.appendChild(rm);
    }
    box.hidden = !box.childNodes.length;
    var rows = document.querySelectorAll('#main-cart-items .cart-item');
    this.cart.items.forEach(function (it, i) {
      if (rows[i]) rows[i].classList.toggle('ce-gone-row', gone.indexOf(it) >= 0);
    });
  };

  Cart.prototype.render = function () {
    var self = this, cart = this.cart, data = this.data;
    this.checkGone();
    if (!cart.item_count) { this.offerBack(); return; }
    var priced = priceCart(cartLines(cart, data), data.rules);
    // in the order of their names (Shopify lists the newest lines first)
    var ids = setIds(cart).sort(function (a, b) { return setNumber(cart, a) - setNumber(cart, b); });
    var rows = document.querySelectorAll('#main-cart-items .cart-item');
    // the theme's rows of a set are hidden; the set shows as one item instead
    cart.items.forEach(function (it, i) {
      var row = rows[i];
      if (row) row.classList.toggle('ce-in-set', !!(it.properties && it.properties._set));
    });
    // single pieces: their own discount on their own rows, in the theme's sale-price markup
    cart.items.forEach(function (it, i) {
      if (rows[i] && !(it.properties && it.properties._set)) {
        markLoose(rows[i], it, priced.lines[it.key] || { discount: 0, unit_discount: 0, percent: 0 });
      }
    });
    if (this.observer) this.observer.takeRecords();      // our own changes, not the theme's
    var host = document.querySelector('[data-ce-sets]');
    if (!host) return;
    host.innerHTML = '';
    ids.forEach(function (sid, n) {
      var lines = setLines(cart, sid);
      var base = null, charms = [], sub = 0, disc = 0;
      lines.forEach(function (it) {
        var p = data.byVariant[String(it.variant_id)];
        sub += it.final_line_price;
        disc += priced.lines[it.key] ? priced.lines[it.key].discount : 0;
        if (!p) return;
        for (var q = 0; q < it.quantity; q++) { if (p.kind === 'pendant') charms.push(p); else base = p; }
      });
      var s = priced.sets.filter(function (x) { return x.set === sid; })[0];
      var card = el('div', 'ce-set');
      var pic = el('canvas', 'ce-set__picture');
      card.appendChild(pic);
      var info = el('div', 'ce-set__info');
      info.appendChild(el('h3', 'ce-set__title', (lines[0].properties.Set) || ('Jewelry set ' + (n + 1))));
      var ul = el('ul', 'ce-set__pieces');
      lines.forEach(function (it) {
        var li = el('li');
        li.appendChild(el('span', '', (it.quantity > 1 ? it.quantity + ' × ' : '') + it.product_title));
        li.appendChild(el('span', '', money(it.final_line_price)));
        ul.appendChild(li);
      });
      info.appendChild(ul);
      var pr = el('div', 'ce-set__price');
      if (s) {
        pr.appendChild(el('span', 'ce-set__was', money(sub)));
        pr.appendChild(el('span', 'ce-set__now', money(sub - disc)));
        pr.appendChild(el('span', 'ce-set__off', fmtPct(s.percent) + ' off'));
      } else {
        var why = priced.invalid.filter(function (x) { return x.set === sid; })[0];
        pr.appendChild(el('span', 'ce-set__now', money(sub)));
        pr.appendChild(el('span', 'ce-set__off is-blocking', 'Not a full set yet: it ' +
          (why ? why.reason : 'needs a chain or bracelet and a charm') + '. Edit it, or remove it, to check out.'));
        card.classList.add('is-blocking');
      }
      info.appendChild(pr);
      var acts = el('div', 'ce-set__actions');
      var edit = el('a', 'link', 'Edit');
      edit.href = self.builder + '?edit=' + encodeURIComponent(sid);
      var rm = el('button', 'link', 'Remove');
      rm.type = 'button';
      rm.addEventListener('click', function () {
        var updates = {};
        lines.forEach(function (it) { updates[it.key] = 0; });
        rm.disabled = true;
        postJSON('/cart/update.js', { updates: updates }).then(function () { location.reload(); });
      });
      acts.appendChild(edit);
      acts.appendChild(rm);
      info.appendChild(acts);
      card.appendChild(info);
      host.appendChild(card);
      if (base && base.mode) draw(pic, base, charms, data.px_mm).catch(function () { pic.hidden = true; });
      else pic.hidden = true;
    });
    host.hidden = !ids.length;
    // the savings, under the theme's subtotal
    var sv = document.querySelector('[data-ce-savings]');
    if (sv) {
      sv.hidden = !priced.discount;
      sv.querySelector('[data-ce-saving]').textContent = '−' + money(priced.discount);
      sv.querySelector('[data-ce-estimate]').textContent = money(priced.total);
      var note = sv.querySelector('[data-ce-loose]');
      if (note) {          // the numbers are catalog/bundles.yaml's, never the page's
        note.hidden = !priced.loose.percent;
        note.textContent = 'More than ' + data.rules.loose_more_than + ' single pieces: each one is ' +
          fmtPct(data.rules.loose_percent) + ' off.';
      }
      var when = sv.querySelector('[data-ce-when]');
      if (when) {
        when.textContent = self.service ? 'Your savings are applied at checkout.' :
          'Not yet applied at checkout: the store\u2019s checkout service is still being set up.';
      }
    }
    // with savings that checkout will apply, the theme's own total (before them) would contradict ours
    var theirs = document.querySelectorAll('#main-cart-footer .totals');
    Array.prototype.forEach.call(theirs, function (x) { x.hidden = !!(priced.discount && self.service && !priced.blocked); });
    var quick = document.querySelectorAll('.cart__dynamic-checkout-buttons');
    Array.prototype.forEach.call(quick, function (q) {
      q.hidden = !!((priced.discount && self.service) || priced.blocked);
    });
    // a set that is not one yet cannot be checked out (operator 2026-10-07)
    var msg = this.root.querySelector('[data-message]');
    if (msg) msg.textContent = priced.blocked ? 'A set in your cart is not complete: edit it or remove it to check out.' : '';
    this.hookCheckout(priced);
  };

  /* With savings to give, checkout goes through the store's checkout service,
     which prices the order by the same rules; without it (or when the service
     cannot be reached) the store's own checkout, at full price. */
  Cart.prototype.hookCheckout = function (priced) {
    var self = this;
    var btns = document.querySelectorAll('#checkout, [name="checkout"]');
    Array.prototype.forEach.call(btns, function (b) {
      if (b._ceHooked) return;
      b._ceHooked = true;
      b.addEventListener('click', function (e) {
        var p = priceCart(cartLines(self.cart, self.data), self.data.rules);
        if ((self.gone || []).length) {                      // a piece no longer for sale: remove it first
          e.preventDefault();
          var g = self.root.querySelector('[data-ce-gone]');
          if (g) g.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        if (p.blocked) {                                     // an unfinished set: no checkout at all
          e.preventDefault();
          var m = self.root.querySelector('[data-message]');
          if (m) { m.textContent = 'A set in your cart is not complete: edit it or remove it to check out.';
                   m.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
          return;
        }
        if (!self.service || !p.discount) return;            // the store's own checkout
        e.preventDefault();
        b.disabled = true;
        postJSON(self.service, { items: self.cart.items.map(function (it) {
          return { variant_id: it.variant_id, quantity: it.quantity, set: (it.properties && it.properties._set) || '',
                   label: (it.properties && it.properties.Set) || '' };
        }), expect: p.total }).then(function (r) {
          if (!r.url) throw new Error('no checkout link');
          if (r.url === '/checkout') { location.href = r.url; return; }   // nothing to give after all
          // the draft order's checkout carries the cart now: keep a copy and empty
          // the cart, so a paid order leaves nothing behind (the copy can come back)
          try {
            localStorage.setItem(HANDOFF, JSON.stringify({ at: Date.now(), items: self.cart.items.map(function (it) {
              return { id: it.variant_id, quantity: it.quantity, properties: it.properties || {} };
            }) }));
          } catch (e2) { /* private window: the cart simply stays */ }
          return postJSON('/cart/clear.js', {}).catch(function () { /* the checkout still opens */ })
            .then(function () { location.href = r.url; });
        }).catch(function (err) {
          b.disabled = false;
          if (/no longer for sale/.test(String(err && err.message))) {     // the checkout service's 409
            self.onStore = {};                                            // ask the store again
            self.checkGone();
            return;
          }
          if (window.confirm('The set prices could not be applied just now. Check out at the regular price?')) {
            location.href = '/checkout';                     // the store's own checkout, from this cart
          }
        });
      });
    });
  };

  // --- every page: a cart swept when the collections change ---------------------------
  // Operator 2026-10-17: "Once the new wave arrives, all the pending shopping carts will be
  // swept to deprecate retired items, and suggesting new ones." A cart is kept in the
  // shopper's browser -- the store can neither list nor change it -- so this runs when the
  // shopper comes back, on any page (the bar at the top of every page loads it): a piece no
  // longer on the store is taken out, the shopper is told which, and pieces of the same kind
  // from the collections on the store now are offered in its place -- into the same place
  // of a set, so the set keeps its saving.
  var SWEPT = 'ce-swept', SWEEP_EVERY = 10 * 60 * 1000;

  /* What a piece is, from its title (a piece gone from the store can no longer be asked):
     a set's place 0 is its chain or bracelet. */
  function kindOf(title, slot) {
    var t = String(title || '');
    if (/bracelet/i.test(t)) return 'bracelet';
    if (/chain|necklace/i.test(t)) return 'chain';
    return slot === 0 ? 'chain' : 'pendant';
  }

  /* Pieces of the same kind on the store now, not in the cart, nearest in price first. */
  function offersFor(gone, pieces, inCart, n) {
    var price = gone.price || 0;
    return pieces.filter(function (p) { return p.kind === gone.kind && !inCart[String(p.variant)]; })
      .sort(function (a, b) { return Math.abs(a.price - price) - Math.abs(b.price - price) || (a.sku < b.sku ? -1 : 1); })
      .slice(0, n || 3);
  }

  function Sweep(node) {
    this.url = node.dataset.data;
    var last = this.load();
    if (last && last.notice) this.show(last.notice);
    if (!last || Date.now() - last.at > SWEEP_EVERY) this.run(last);
  }

  Sweep.prototype.load = function () {
    try { return JSON.parse(sessionStorage.getItem(SWEPT) || 'null'); } catch (e) { return null; }
  };

  Sweep.prototype.keep = function (notice) {
    try { sessionStorage.setItem(SWEPT, JSON.stringify({ at: Date.now(), notice: notice || null })); } catch (e) { /* not kept */ }
  };

  Sweep.prototype.run = function (last) {
    var self = this, told = last && last.notice;
    getJSON('/cart.js').then(function (cart) {
      var on = {}, handles = cart.items.map(function (it) { return it.handle; })
        .filter(function (h, i, all) { return h && all.indexOf(h) === i; });
      return Promise.all(handles.map(function (h) {
        return fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { on[h] = r.status !== 404; })
          .catch(function () { on[h] = true; });             // not known: left in the cart
      })).then(function () {
        var gone = cart.items.filter(function (it) { return on[it.handle] === false; });
        if (!gone.length) { self.keep(told); return; }
        var updates = {};
        gone.forEach(function (it) { updates[it.key] = 0; });
        return postJSON('/cart/update.js', { updates: updates }).then(function (after) {
          return getJSON(self.url).catch(function () { return { pieces: [] }; }).then(function (data) {
            var inCart = {};
            ((after && after.items) || []).forEach(function (it) { inCart[String(it.variant_id)] = true; });
            var removed = gone.map(function (it) {
              var pr = it.properties || {}, slot = pr._set ? Number(pr._pos || 0) : null;
              var r = { title: it.product_title, set: pr.Set || '', price: it.price, kind: kindOf(it.product_title, slot),
                        slot: pr._set ? { _set: pr._set, _pos: String(pr._pos || 0), Set: pr.Set || '' } : null };
              // no piece offered twice: four charms gone from one set got the same three
              // offers four times over (10-18)
              r.offers = offersFor(r, data.pieces || [], inCart, 3).map(function (p) {
                inCart[String(p.variant)] = true;
                return { variant: p.variant, title: p.title, price: p.price, thumb: p.thumb || p.file };
              });
              return r;
            });
            var notice = { removed: removed.concat((told && told.removed) || []).slice(0, 8) };
            self.keep(notice);
            self.show(notice);
            document.dispatchEvent(new CustomEvent('ce-cart-changed'));
            if (/^\/cart\/?$/.test(location.pathname)) location.reload();   // the theme's own rows
          });
        });
      });
    }).catch(function () { /* the store's own cart still stands */ });
  };

  Sweep.prototype.show = function (notice) {
    var self = this, box = document.querySelector('[data-ce-swept]');
    if (!box) {
      box = el('div', 'ce-swept');
      box.setAttribute('data-ce-swept', '');
      box.setAttribute('role', 'status');
      var main = document.querySelector('main') || document.body;
      main.insertBefore(box, main.firstChild);
    }
    box.innerHTML = '';
    // One quiet line on any page, the offers folded under it (open on the cart page):
    // operator 2026-10-18 on the first form -- four charms of one set made four blocks with
    // the same three offers each, in a pink box over the homepage's cover: "quite absurd and
    // messed up the overall vibe of the page".
    var onCart = /^\/cart\/?$/.test(location.pathname);
    box.classList.toggle('ce-swept--open', onCart);
    var close = el('button', 'ce-swept__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', function () { self.keep(null); box.remove(); });
    box.appendChild(close);
    var removed = notice.removed, sets = [];
    removed.forEach(function (r) { if (r.set && sets.indexOf(r.set) < 0) sets.push(r.set); });
    var n = removed.length, what;
    if (n === 1) what = removed[0].title + (removed[0].set ? ' (from your ' + removed[0].set + ')' : '') + ' is';
    else what = n + ' pieces' + (sets.length === 1 && removed.every(function (r) { return r.set; }) ? ' from your ' + sets[0] : '') + ' are';
    var lead = el('p', 'ce-swept__lead', 'Our collections have changed: ' + what + ' no longer available, so we took ' +
      (n > 1 ? 'them' : 'it') + ' out of your cart. ');
    // a set's pieces together, their offers pooled, each place of the set filled in turn
    var groups = [];
    removed.forEach(function (r) {
      var key = r.set || ('\u0000' + r.title), g = null;
      for (var i = 0; i < groups.length; i++) if (groups[i].key === key) g = groups[i];
      if (!g) { g = { key: key, set: r.set, removed: [], offers: [] }; groups.push(g); }
      g.removed.push(r);
      (r.offers || []).forEach(function (o) {
        if (!g.offers.some(function (x) { return String(x.variant) === String(o.variant); })) g.offers.push(o);
      });
    });
    var any = groups.some(function (g) { return g.offers.length; });
    var label = function (open) { return open ? 'Hide' : 'See what’s new in its place'; };
    if (any) {
      var more = el('button', 'ce-swept__more', label(onCart));
      more.type = 'button';
      more.setAttribute('aria-expanded', onCart ? 'true' : 'false');
      more.addEventListener('click', function () {
        var open = box.classList.toggle('ce-swept--open');
        more.textContent = label(open);
        more.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      lead.appendChild(more);
    }
    box.appendChild(lead);
    if (!any) return;
    var detail = el('div', 'ce-swept__detail');
    groups.forEach(function (g) {
      if (!g.offers.length) return;
      var row = el('div', 'ce-swept__row');
      var titles = g.removed.map(function (r) { return r.title; }).join(', ');
      row.appendChild(el('p', 'ce-swept__for', g.set
        ? 'For your ' + g.set + ' (' + titles + '), in ' + (g.removed.length > 1 ? 'their' : 'its') + ' place:'
        : 'In place of ' + titles + ':'));
      var list = el('div', 'ce-swept__offers');
      g.offers.slice(0, Math.max(3, g.removed.length + 1)).forEach(function (o) {
        var card = el('div', 'ce-swept__offer');
        if (o.thumb) { var im = el('img'); im.src = o.thumb; im.alt = ''; im.loading = 'lazy'; card.appendChild(im); }
        card.appendChild(el('span', 'ce-swept__title', o.title));
        card.appendChild(el('span', 'ce-swept__price', money(o.price)));
        var add = el('button', 'button button--secondary', 'Add');
        add.type = 'button';
        add.addEventListener('click', function () {
          add.disabled = true;
          var open = null;                                     // the next place of the set still empty
          for (var i = 0; i < g.removed.length && !open; i++) if (!g.removed[i].filled) open = g.removed[i];
          postJSON('/cart/add.js', { items: [{ id: o.variant, quantity: 1, properties: (open && open.slot) || {} }] }).then(function () {
            add.textContent = 'Added';
            if (open) { open.filled = true; open.offers = []; }
            g.removed.forEach(function (r) {                  // not offered again after a reload
              r.offers = (r.offers || []).filter(function (x) { return String(x.variant) !== String(o.variant); });
            });
            if (g.removed.every(function (r) { return r.filled; })) {   // every place filled
              Array.prototype.forEach.call(row.querySelectorAll('button'), function (x) { x.disabled = true; });
            }
            self.keep(notice);
            document.dispatchEvent(new CustomEvent('ce-cart-changed'));
            if (/^\/cart\/?$/.test(location.pathname)) location.reload();
          }).catch(function (e) { add.disabled = false; add.textContent = 'Could not add: ' + e.message; });
        });
        card.appendChild(add);
        list.appendChild(card);
      });
      row.appendChild(list);
      detail.appendChild(row);
    });
    box.appendChild(detail);
  };

  // --- start ------------------------------------------------------------------------

  var api = { priceCart: priceCart, placements: placements, lineAt: lineAt, kindOf: kindOf, offersFor: offersFor };

  function boot() {
    // loaded twice on the builder and cart pages (their section, and the bar on every page)
    if (root.__ceBooted) return;
    root.__ceBooted = true;
    var sweep = document.querySelector('[data-ce-sweep]');
    if (sweep) new Sweep(sweep);
    var nodes = document.querySelectorAll('[data-ce-bundle], [data-ce-cart-sets]');
    if (!nodes.length) return;
    var url = nodes[0].dataset.data;
    getJSON(url).then(function (data) {
      indexData(data);
      Array.prototype.forEach.call(nodes, function (n) {
        if (n.hasAttribute('data-ce-bundle')) new Builder(n, data);
        else new Cart(n, data);
      });
    }).catch(function (e) {
      Array.prototype.forEach.call(nodes, function (n) {
        var m = n.querySelector('[data-message]');
        if (m) m.textContent = 'The builder could not load: ' + e.message;
      });
    });
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.CEBundle = api;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }
})(typeof window !== 'undefined' ? window : globalThis);
